import { fromPotentialScope } from "core/components/ReactHookFormComponents/ReactHookScopedRoles/useScopedRolesForm";
import type { ScopedRoleDto, ScopedRoleEntry } from "core/entities/ScopeDefs";
import { uuid } from "core/utils/uuid";
import type {
  ArticleDto,
  ArticleStatus,
  ArticleTranslationCreateDto,
  ArticleTranslationUpdateDto,
  BannerCreateDto,
  BannerFormValue,
  BannerUpdateDto,
  PublicationArticleCreateDto,
  PublicationArticleFormValuesTranslation,
  PublicationArticleUpdateDto,
  PublicationEventArticleFormValues,
  PublicationNewsArticleFormValues,
} from "../resources/NewsEventsDefs";

export function mapBannerFormToCreateDto(
  formBanner: BannerFormValue
): BannerCreateDto | undefined {
  if (!formBanner.bannerFile || !formBanner.bannerFile.bannerId) {
    return undefined;
  }
  return {
    blobName: formBanner.bannerFile?.bannerId ?? "",
    blurDataUrl: formBanner.bannerFile?.bannerBlobDataUrl ?? "",
    height: formBanner.bannerFile?.bannerHeight ?? 0,
    resolution: 0,
    width: formBanner.bannerFile?.bannerWidth ?? 0,
    altText: formBanner.altText,
    description: formBanner.description,
  };
}

export function mapBannerFormToUpdateDto(
  formBanner: BannerFormValue
): BannerUpdateDto | undefined {
  if (!formBanner.bannerFile || !formBanner.bannerFile.bannerId) {
    return undefined;
  }
  return {
    id: uuid(), // maybe should use the actual banner ID if available
    blobName: formBanner.bannerFile?.bannerId ?? "",
    blurDataUrl: formBanner.bannerFile?.bannerBlobDataUrl ?? "",
    height: formBanner.bannerFile?.bannerHeight ?? 0,
    resolution: 0,
    width: formBanner.bannerFile?.bannerWidth ?? 0,
    altText: formBanner.altText,
    description: formBanner.description,
  };
}

export function mapScopedRoleEntryToDto(input: ScopedRoleEntry): ScopedRoleDto {
  return {
    isCorporate: input.type === "Corporate",
    roleId: input.roleId,
    brandId: input.brandId,
    countryId: input.countryId,
    provinceStateId: input.provinceStateId,
    shopId: input.shopId,
  };
}

export function mapToNewsArticleFormToCreateDto(
  formValues: PublicationNewsArticleFormValues,
  status: ArticleStatus
): PublicationArticleCreateDto {
  return {
    availableFrom: formValues?.availableFrom?.toISOString() || null,
    availableTo: formValues?.availableTo?.toISOString() || null,
    isFeatured: true, // as per user requirements
    status: status,
    categoryId: formValues.categoryId || null,
    typeId: formValues.labelId || "",
    publishedTime:
      formValues?.availableFrom?.toISOString() || new Date().toISOString(),
    bannerId: null, // provided by the backend
    articleBanner: mapBannerFormToCreateDto(formValues.banner),
    articlePermission: undefined,
    articleTranslations: formValues.translations.map(
      mapTranslationFormToCreateDto
    ),
    scopedRoles: [
      ...formValues.corporateScopes.values(),
      ...formValues.shopScopes.values(),
    ].map(mapScopedRoleEntryToDto),
    // the following fields are specific to events
    startDate: null,
    endDate: null,
    url: null,
    address: null,
    locationDescription: null,

    isImportant: formValues.isImportant,
    isPublic: formValues.visibleToOtherBrandsAndRegions,
  };
}

export function mapToEventArticleFormToCreateDto(
  formValues: PublicationEventArticleFormValues,
  status: ArticleStatus
): PublicationArticleCreateDto {
  return {
    ...mapToNewsArticleFormToCreateDto(formValues, status),
    // the following fields are specific to events
    startDate: formValues.startDate?.toISOString() || null,
    endDate: formValues.endDate?.toISOString() || null,
    url: formValues.url || null,
    address: formValues.address || null,
    locationDescription: formValues.locationDescription || null,
  };
}

export function mapTranslationFormToUpdateDto(
  input: PublicationArticleFormValuesTranslation
): ArticleTranslationUpdateDto {
  return {
    blurb: input.blurb,
    content: input.content,
    locale: input.locale,
    tags: input.tags,
    title: input.title,
    buttonText: input.buttonText,
    buttonUrl: input.buttonUrl,
    hasCTA: input.includeCallToActionButton,
    isMailchimpImport: false,
    bannerId: null, // provided by the backend
    translationBanner: mapBannerFormToUpdateDto(input.banner)!,
  };
}

export function mapTranslationFormToCreateDto(
  input: PublicationArticleFormValuesTranslation
): ArticleTranslationCreateDto {
  return {
    blurb: input.blurb,
    content: input.content,
    locale: input.locale,
    tags: input.tags,
    title: input.title,
    buttonText: input.buttonText,
    buttonUrl: input.buttonUrl,
    hasCTA: input.includeCallToActionButton,
    isMailchimpImport: false,
    bannerId: null, // provided by the backend
    translationBanner: mapBannerFormToCreateDto(input.banner)!,
  };
}

export function mapArticleDtoToUpdateDto(
  data: ArticleDto
): PublicationArticleUpdateDto {
  const article = structuredClone(data);
  return {
    availableFrom: article.availableFrom ?? null,
    availableTo: article.availableTo ?? null,
    isFeatured: article.isFeatured ?? false,
    isPublic: article.isPublic ?? false,
    status: article.status,
    categoryId: article.categoryId ?? null,
    typeId: article.articleType?.id ?? "",
    publishedTime: article.publishedTime,
    bannerId: article.bannerId ?? null,
    articleBanner: {
      id: article.articleBanner?.id ?? "",
      blobName: article.articleBanner?.blobName ?? "",
      description: article.articleBanner?.description,
      altText: article.articleBanner?.altText,
      height: article.articleBanner?.height ?? 0,
      width: article.articleBanner?.width ?? 0,
      resolution: article.articleBanner?.resolution ?? 0,
      blurDataUrl: article.articleBanner?.blurDataUrl ?? "",
    },
    articleTranslations:
      article.articleTranslations?.map((t) => {
        return {
          bannerId: t.bannerId ?? null,
          blurb: t.blurb ?? "",
          content: t.content ?? "",
          hasCTA: t.hasCTA ?? false,
          locale: t.locale ?? "",
          title: t.title ?? "",
          tags: t.tags ?? [],
          buttonText: t.buttonText ?? "",
          buttonUrl: t.buttonUrl ?? "",
          isMailchimpImport: t.isMailchimpImport ?? false,
          translationBanner: t.translationBanner
            ? {
                id: t.translationBanner?.id ?? "",
                blobName: t.translationBanner?.blobName ?? "",
                description: t.translationBanner?.description,
                altText: t.translationBanner?.altText ?? "",
                height: t.translationBanner?.height ?? 0,
                width: t.translationBanner?.width ?? 0,
                resolution: t.translationBanner?.resolution ?? 0,
                blurDataUrl: t.translationBanner?.blurDataUrl ?? "",
              }
            : undefined,
        };
      }) ?? [],
    startDate: article.startDate ?? null,
    endDate: article.endDate ?? null,
    url: article.url ?? null,
    address: article.address ?? null,
    scopedRoles:
      article.scopedRoles?.map((sr) => ({
        isCorporate: sr?.isCorporate,
        roleId: sr?.roleId,
        brandId: sr?.brandId,
        countryId: sr?.countryId,
        provinceStateId: sr?.provinceStateId,
        shopId: sr?.shopId,
        userId: sr?.userId,
        documentId: sr?.documentId,
        newsEventId: sr?.newsEventId,
        menuItemId: sr?.menuItemId,
        title: sr?.title,
        brcyCode: sr?.brcyCode,
        concurrencyStamp: sr?.concurrencyStamp,
        isShopRole: sr?.isShopRole,
        id: sr?.id,
      })) ?? null,
    isImportant: article.isImportant ?? false,
    locationDescription: article.locationDescription ?? null,
  };
}

export function mapNewsArticleFormToUpdateDto(
  formValues: PublicationNewsArticleFormValues,
  status: ArticleStatus
): PublicationArticleUpdateDto {
  return {
    availableFrom: formValues?.availableFrom?.toISOString() || null,
    availableTo: formValues?.availableTo?.toISOString() || null,
    isFeatured: true, // as per user requirements
    isPublic: formValues.visibleToOtherBrandsAndRegions,
    status: status,
    categoryId: formValues.categoryId || null,
    typeId: formValues.labelId || "",
    publishedTime:
      formValues?.availableFrom?.toISOString() || new Date().toISOString(),
    bannerId: null, // provided by the backend
    articleBanner: mapBannerFormToUpdateDto(formValues.banner)!,
    articlePermission: undefined,
    articleTranslations: formValues.translations.map(
      mapTranslationFormToUpdateDto
    ),
    isImportant: formValues.isImportant,
    scopedRoles: [
      ...formValues.corporateScopes.values(),
      ...formValues.shopScopes.values(),
    ].map(mapScopedRoleEntryToDto),
    // the following fields are specific to events
    startDate: null,
    endDate: null,
    url: null,
    address: null,
    locationDescription: null,
  };
}
export function mapEventArticleFormToUpdateDto(
  formValues: PublicationEventArticleFormValues,
  status: ArticleStatus
) {
  return {
    ...mapNewsArticleFormToUpdateDto(formValues, status),
    startDate: formValues.startDate?.toISOString() || null,
    endDate: formValues.endDate?.toISOString() || null,
    url: formValues.url || null,
    address: formValues.address || null,
    locationDescription: formValues.locationDescription || null,
  };
}

function processScopedRoles(input: ScopedRoleDto[]) {
  const corporateScopes = new Map<string, ScopedRoleEntry>();
  const shopScopes = new Map<string, ScopedRoleEntry>();
  input.forEach((scopedRole) => {
    const entry = fromPotentialScope({
      type: scopedRole.isCorporate ? "Corporate" : "Shop",
      roleId: scopedRole.roleId ?? null,
      brandId: scopedRole.brandId ?? null,
      countryId: scopedRole.countryId ?? null,
      provinceStateId: scopedRole.provinceStateId ?? null,
      shopId: scopedRole.shopId ?? null,
    });
    if (scopedRole.isCorporate) {
      corporateScopes.set(entry.localId, entry);
    } else {
      shopScopes.set(entry.localId, entry);
    }
  });
  return { corporateScopes, shopScopes };
}

export function mapArticleDtoToNewsArticleFormValues(
  data: ArticleDto,
  currentLocale: string
): PublicationNewsArticleFormValues {
  const scopedRoleDtos =
    data?.scopedRoles?.filter(
      (sr): sr is ScopedRoleDto => sr !== null && sr !== undefined
    ) || [];
  const { corporateScopes, shopScopes } = processScopedRoles(scopedRoleDtos);
  const currentTabValue =
    data.articleTranslations?.find(
      (translation) => translation.locale === currentLocale
    )?.locale ||
    data.articleTranslations?.[0]?.locale ||
    "en";

  return {
    categoryId: data.categoryId || "",
    labelId: data.typeId || "",
    visibleToOtherBrandsAndRegions: data.isPublic || false,
    corporateScopes: corporateScopes,
    shopScopes: shopScopes,
    banner: {
      bannerFile: data.articleBanner
        ? {
            bannerId: data.articleBanner.blobName || "",
            bannerBlobDataUrl: data.articleBanner.blurDataUrl || "",
            bannerHeight: data.articleBanner.height || 0,
            bannerWidth: data.articleBanner.width || 0,
          }
        : null,
      altText: data.articleBanner?.altText || "",
      description: data.articleBanner?.description || "",
    },
    currentTabValue,
    translations:
      data.articleTranslations?.map((translation) => ({
        locale: translation.locale || "en",
        title: translation.title || "",
        content: translation.content || "",
        tags: translation.tags || [],
        blurb: translation.blurb || "",
        includeCallToActionButton: translation.hasCTA || false,
        buttonText: translation.buttonText || "",
        buttonUrl: translation.buttonUrl || "",
        banner: {
          bannerFile: translation.translationBanner
            ? {
                bannerId: translation.translationBanner.blobName || "",
                bannerBlobDataUrl:
                  translation.translationBanner.blurDataUrl || "",
                bannerHeight: translation.translationBanner.height || 0,
                bannerWidth: translation.translationBanner.width || 0,
              }
            : null,
          altText: translation.translationBanner?.altText || "",
          description: translation.translationBanner?.description || "",
        },
      })) || [],
    isImportant: data.isImportant || false,
    availableFrom: data.availableFrom ? new Date(data.availableFrom) : null,
    availableTo: data.availableTo ? new Date(data.availableTo) : null,
  };
}

function processDate(dateString: string | null | undefined): Date | null {
  try {
    return dateString ? new Date(dateString) : null;
  } catch {
    return null;
  }
}

export function mapArticleDtoToEventArticleFormValues(
  data: ArticleDto,
  currentLocale: string
): PublicationEventArticleFormValues {
  return {
    ...mapArticleDtoToNewsArticleFormValues(data, currentLocale),
    startDate: processDate(data.startDate),
    endDate: processDate(data.endDate),
    address: data.address || "",
    locationDescription: data.locationDescription || "",
    url: data.url || "",
  };
}

export function createDefaultArticleTranslationFormValue(
  locale: string
): PublicationArticleFormValuesTranslation {
  return {
    locale,
    title: "",
    content: "",
    tags: [],
    blurb: "",
    includeCallToActionButton: false,
    buttonText: "",
    buttonUrl: "",
    banner: { bannerFile: null, altText: "", description: "" },
  };
}
